import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Upload, CheckCircle, XCircle, Pencil, Trash2, Globe, Minus } from "lucide-react";
import { memoryService, Memory, MemoryStatus } from "../services/memory.service";
import { useToast } from "../context/ToastContext";
import Pagination from "../components/Pagination";
import CreateKnowledgeModal from "../components/CreateKnowledgeModal";
import EditKnowledgeModal from "../components/EditKnowledgeModal";
import DeleteKnowledgeModal from "../components/DeleteKnowledgeModal";
import ImportKnowledgeModal from "../components/ImportKnowledgeModal";
import "../styles/clients.css";

export default function KnowledgeBasePage() {
    const { clientId } = useParams<{ clientId: string }>();

    const [entries, setEntries] = useState<Memory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [statusFilter, setStatusFilter] = useState<MemoryStatus | "">("");
    const { showToast } = useToast();

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [showCreate, setShowCreate] = useState(false);
    const [editEntry, setEditEntry] = useState<Memory | null>(null);
    const [deleteEntry, setDeleteEntry] = useState<Memory | null>(null);
    const [showImport, setShowImport] = useState(false);
    const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkLoading, setBulkLoading] = useState(false);

    const selectAllRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate =
                selectedIds.size > 0 && selectedIds.size < entries.length;
        }
    }, [selectedIds.size, entries.length]);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setPage(1);
    }, [statusFilter]);

    const fetchEntries = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        setError("");
        try {
            const res = await memoryService.getByClient(clientId, {
                status: statusFilter || undefined,
                page,
                limit,
            });
            setEntries(res.data);
            setTotal(res.meta.total);
            setTotalPages(res.meta.totalPages);
            setSelectedIds(new Set());
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to load knowledge base.");
        } finally {
            setLoading(false);
        }
    }, [clientId, statusFilter, page, limit]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const handlePageChange = (p: number) => {
        setPage(Math.max(1, Math.min(p, totalPages || 1)));
    };

    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    };

    const handleStatusToggle = async (entry: Memory) => {
        const newStatus: MemoryStatus = entry.status === "DRAFT" ? "APPROVED" : "DRAFT";
        try {
            await memoryService.updateStatus(entry.id, newStatus);
            fetchEntries();
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to update status.");
        }
    };

    const handleBulkStatus = async (status: MemoryStatus) => {
        if (selectedIds.size === 0) return;
        setBulkLoading(true);
        setError("");
        try {
            const result = await memoryService.bulkUpdateStatus(Array.from(selectedIds), status);
            let msg = `${result.updated} entries ${status === "APPROVED" ? "approved" : "set as draft"}`;
            if (result.notFound?.length > 0) {
                msg += `. ${result.notFound.length} not found.`;
            }
            showToast(msg);
            fetchEntries();
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to update statuses.");
        } finally {
            setBulkLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setBulkDeleteLoading(true);
        setError("");
        try {
            const result = await memoryService.bulkDelete(Array.from(selectedIds));
            let msg = `${result.deleted} entries deleted`;
            if (result.notFound?.length > 0) {
                msg += `. ${result.notFound.length} not found.`;
            }
            showToast(msg);
            setBulkDeleteConfirm(false);
            fetchEntries();
        } catch (err: any) {
            setError(err.response?.data?.message || err.response?.data?.msg || "Failed to delete entries.");
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === entries.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(entries.map((e) => e.id)));
        }
    };

    const truncate = (text: string, max = 80) =>
        text.length > max ? text.slice(0, max) + "..." : text;

    if (!clientId) return null;

    return (
        <>
            <div className="clients-container">
                <div className="clients-header">
                    <Link to={`/clients/${clientId}`} className="btn-back">
                        <ArrowLeft size={16} />
                        <span>Back to client</span>
                    </Link>
                    <h1>Knowledge Base</h1>
                </div>

                <div className="kb-filter-bar">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as MemoryStatus | "")}
                    >
                        <option value="">All statuses</option>
                        <option value="DRAFT">Draft</option>
                        <option value="APPROVED">Approved</option>
                    </select>

                    <div className="clients-header-actions">
                        <button className="btn-create" onClick={() => setShowCreate(true)}>
                            <Plus size={16} /> Add Knowledge
                        </button>
                        <button className="btn-create" onClick={() => setShowImport(true)}>
                            <Upload size={16} /> Import
                        </button>
                    </div>
                </div>

                {error && <div className="modal-error" style={{ marginBottom: 16 }}>{error}</div>}

                {loading ? (
                    <div className="clients-loading">
                        <div className="auth-spinner" />
                    </div>
                ) : total === 0 ? (
                    <div className="clients-empty">
                        <h3>No knowledge entries yet</h3>
                        <p>Add Q&A entries to power your chatbot's knowledge base.</p>
                        <button className="btn-create" onClick={() => setShowCreate(true)}>
                            <Plus size={16} /> Add Knowledge
                        </button>
                    </div>
                ) : (
                    <>
                    {selectedIds.size > 0 && (
                        <div className="bulk-action-bar">
                            <span>{selectedIds.size} selected</span>
                            <button className="btn-bulk approve" onClick={() => handleBulkStatus("APPROVED")} disabled={bulkLoading}>
                                <CheckCircle size={14} /> Approve
                            </button>
                            <button className="btn-bulk draft" onClick={() => handleBulkStatus("DRAFT")} disabled={bulkLoading}>
                                <XCircle size={14} /> Draft
                            </button>
                            <button className="btn-bulk delete" onClick={() => setBulkDeleteConfirm(true)} disabled={bulkLoading}>
                                <Trash2 size={14} /> Delete
                            </button>
                            <button className="btn-bulk clear" onClick={() => setSelectedIds(new Set())}>
                                Clear
                            </button>
                        </div>
                    )}
                    <div className="clients-table-card">
                        <table className="clients-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input
                                            ref={selectAllRef}
                                            type="checkbox"
                                            checked={selectedIds.size === entries.length && entries.length > 0}
                                            onChange={toggleSelectAll}
                                            className="kb-row-checkbox"
                                        />
                                    </th>
                                    <th>Question</th>
                                    <th>Answer</th>
                                    <th>Category</th>
                                    <th>Status</th>
                                    <th>Public</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry) => (
                                    <tr key={entry.id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(entry.id)}
                                                onChange={() => toggleSelect(entry.id)}
                                                className="kb-row-checkbox"
                                            />
                                        </td>
                                        <td className="kb-cell-truncate" title={entry.question}>
                                            {truncate(entry.question)}
                                        </td>
                                        <td className="kb-cell-truncate" title={entry.answer}>
                                            {truncate(entry.answer)}
                                        </td>
                                        <td>
                                            {entry.metadata?.category ? (
                                                <span className="client-type-badge">{entry.metadata.category}</span>
                                            ) : (
                                                <span className="client-type-badge empty">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`kb-status-badge ${entry.status === "DRAFT" ? "draft" : "approved"}`}>
                                                {entry.status}
                                            </span>
                                        </td>
                                        <td>
                                            {entry.metadata?.isPublic ? (
                                                <Globe size={16} color="#38a169" />
                                            ) : (
                                                <Minus size={16} color="#a0aec0" />
                                            )}
                                        </td>
                                        <td>
                                            <div className="client-actions">
                                                <button className="btn-action" title={entry.status === "DRAFT" ? "Approve" : "Unapprove"} onClick={() => handleStatusToggle(entry)}>
                                                    {entry.status === "DRAFT" ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                                </button>
                                                <button className="btn-action" title="Edit" onClick={() => setEditEntry(entry)}>
                                                    <Pencil size={14} />
                                                </button>
                                                <button className="btn-action danger" title="Delete" onClick={() => setDeleteEntry(entry)}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        total={total}
                        limit={limit}
                        onPageChange={handlePageChange}
                        onLimitChange={handleLimitChange}
                    />
                    </>
                )}
            </div>

            {showCreate && (
                <CreateKnowledgeModal
                    clientId={clientId}
                    onClose={() => setShowCreate(false)}
                    onCreated={() => { setShowCreate(false); fetchEntries(); }}
                />
            )}
            {editEntry && (
                <EditKnowledgeModal
                    entry={editEntry}
                    onClose={() => setEditEntry(null)}
                    onUpdated={() => { setEditEntry(null); fetchEntries(); }}
                />
            )}
            {deleteEntry && (
                <DeleteKnowledgeModal
                    entry={deleteEntry}
                    onClose={() => setDeleteEntry(null)}
                    onDeleted={() => { setDeleteEntry(null); showToast("Entry deleted"); fetchEntries(); }}
                />
            )}
            {showImport && (
                <ImportKnowledgeModal
                    clientId={clientId}
                    onClose={() => setShowImport(false)}
                    onImported={() => fetchEntries()}
                />
            )}
            {bulkDeleteConfirm && (
                <div className="modal-overlay" onClick={() => setBulkDeleteConfirm(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <h2>Confirm Delete</h2>
                        <p className="modal-subtitle">This action cannot be undone</p>
                        <div className="delete-warning">
                            Are you sure you want to delete <strong>{selectedIds.size}</strong> entries?
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="btn-modal-cancel" onClick={() => setBulkDeleteConfirm(false)}>Cancel</button>
                            <button type="button" className="btn-modal-danger" onClick={handleBulkDelete} disabled={bulkDeleteLoading}>
                                {bulkDeleteLoading ? "Deleting..." : `Delete ${selectedIds.size} entries`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
